import * as React from "react";
import { Button, Checkbox, FormControlLabel, Grid, Typography } from "@material-ui/core";
import { useFormik } from "formik";
import { nameof } from "ts-simple-nameof";
import { Link as RouterLink } from "react-router-dom";
import { Form } from "../forms";
import StoryEditorAboutSection from "../stories/StoryEditorAboutSection";
import StoryEditorLinkedItemsSection from "../stories/StoryEditorLinkedItemsSection";
import StoryEditorProjectSection from "../stories/StoryEditorProjectSection";
import StoryEditorImagesSection from "../stories/StoryEditorImagesSection";
import StoryEditorPublishSection from "../stories/StoryEditorPublishSection";
import { BeaconWrapUpStoryInput, BeaconStoryInput } from "../../../api/graphQL/mutationModels";
import { BeaconItem } from "../../../api/graphQL/queryModels";

export interface ComponentProps {
    beaconWrapUpStory: BeaconWrapUpStoryInput;
    beaconItemSummaries: BeaconItem[];
    onSave: (story: BeaconWrapUpStoryInput) => unknown;
    isSaving: boolean;
    clientId: number | null;
    isNew: boolean;
}

const WrapUpStoryEditor = (props: ComponentProps) => {
    const [reviewConfirmed, setReviewConfirmed] = React.useState(false);

    const { beaconWrapUpStory, onSave, isSaving, clientId, isNew } = props;

    const formik = useFormik<BeaconWrapUpStoryInput>({
        initialValues: {
            ...beaconWrapUpStory
        },
        validate: (values) => {
            const errors: Record<string, string> = {};
            if (!values.title?.trim()) {
                errors.title = "Title is required";
            }
            if (!values.description?.trim()) {
                errors.description = "Description is required";
            }
            return errors;
        },
        onSubmit: (story) => {
            onSave(story);
        }
    });

    if (!props.beaconItemSummaries) {
        return null;
    }

    return (
        beaconWrapUpStory && (
            <Form onSubmit={formik.handleSubmit} fullWidth>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Typography variant="h4"> {isNew ? "Create a new wrap-up story" : "Edit wrap-up story"}</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <StoryEditorAboutSection
                            title={formik.values.title}
                            previewDescription={formik.values.previewDescription}
                            description={formik.values.description}
                            handleChange={formik.handleChange}
                            setFieldValue={formik.setFieldValue}
                        />
                        {formik.errors.title && formik.touched.title && (
                            <Typography color="error" variant="caption">
                                {formik.errors.title}
                            </Typography>
                        )}
                        {formik.errors.description && formik.touched.description && (
                            <Typography color="error" variant="caption">
                                {formik.errors.description}
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <StoryEditorImagesSection
                            value={formik.values.images}
                            onChange={(value) => {
                                formik.setFieldValue(
                                    nameof<BeaconWrapUpStoryInput>((s) => s.images),
                                    value
                                );
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <StoryEditorProjectSection
                            story={formik.values as unknown as BeaconStoryInput}
                            clientId={clientId}
                            setFieldValue={formik.setFieldValue}
                            handleChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <StoryEditorLinkedItemsSection
                            value={formik.values.selectedItems}
                            onChange={(value) => {
                                formik.setFieldValue(
                                    nameof<BeaconWrapUpStoryInput>((s) => s.selectedItems),
                                    value
                                );
                            }}
                            itemSummaries={props.beaconItemSummaries}
                        />
                    </Grid>
                    {formik.values.isPublished && (
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={reviewConfirmed} onChange={(e) => setReviewConfirmed(e.target.checked)} />}
                                label="Reviewed for accuracy?"
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} container>
                        <Grid item>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSaving || (formik.values.isPublished && !reviewConfirmed)}
                                style={{ width: "10rem" }}
                            >
                                {formik.values.isPublished ? "Publish story" : "Save Unreviewed"}
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button component={RouterLink} to={`/?view=stories`}>
                                Cancel
                            </Button>
                        </Grid>
                        <Grid item>
                            <StoryEditorPublishSection
                                isPublished={formik.values.isPublished}
                                onPublishedChange={(isPublished) =>
                                    formik.setFieldValue(
                                        nameof<BeaconWrapUpStoryInput>((s) => s.isPublished),
                                        isPublished
                                    )
                                }
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Form>
        )
    );
};

export default WrapUpStoryEditor;
